// src/components/operations/standards/standards_structure.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import UIkit from "uikit";
import standardsLibrary from "./ruebarue_standards_library.json";
import { AppContexts } from "../../../providers";
import Tags from "../../common/Tags";

const LS_KEY = "ruebarue_org_standards_v1";
const safeArray = (v) => (Array.isArray(v) ? v : []);
const entityKey = (x) => x?.key || x?.id || x?.value || null;
const labelFor = (x) => x?.label || x?.name || x?.title || entityKey(x) || "—";
const stableKey = (prefix, k, idx) => `${prefix}-${k || "na"}-${idx}`;

const deriveDefaultTasksForItem = (item) => {
  const itemLabel = item?.label || item?.key || "Item";
  const fsk = safeArray(item?.default_field_set_keys);

  // simple, practical starter rule-set
  const tasks = [];

  if (fsk.length) {
    tasks.push({
      key: `inspect_${item.key}`,
      label: `Inspect ${itemLabel}`,
      task_type_key: "inspect",
      field_set_keys: fsk,
    });

    // if it includes cleanliness, also add a clean task
    if (fsk.includes("cleanliness_check")) {
      tasks.push({
        key: `clean_${item.key}`,
        label: `Clean ${itemLabel}`,
        task_type_key: "clean",
        field_set_keys: Array.from(new Set(["cleanliness_check", "notes_only", "photo_only"].filter(Boolean))),
      });
    }
  }

  return tasks;
};

const buildOrgStandardsFromLibrary = () => ({
  version: standardsLibrary.version,
  library_name: standardsLibrary.library_name,
  task_types: standardsLibrary.defaults?.task_types || [],
  field_sets: standardsLibrary.defaults?.field_sets || [],
  space_groups: standardsLibrary.defaults?.space_groups || [],
  space_types: standardsLibrary.defaults?.space_types || [],
  item_types: (standardsLibrary.defaults?.item_types || []).map((it) => ({
    ...it,
    checks: safeArray(it.checks),
    default_tasks: safeArray(it.default_tasks).length ? safeArray(it.default_tasks) : deriveDefaultTasksForItem(it),
  })),
  checklist_templates: standardsLibrary.defaults?.checklist_templates || [],
});

const groupForSpaceType = (st) => (st?.group_key || st?.group || st?.area || "indoor").toLowerCase();
const bucketForGroup = (groupKey) => (groupKey === "outdoor" ? "outdoor" : "indoor");

const slugifyKey = (str) =>
  String(str || "")
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);

const uniqueKey = (base, existsSet) => {
  let k = base || "item";
  let i = 2;
  while (existsSet.has(k)) {
    k = `${base}_${i++}`;
  }
  return k;
};

const defaultItemKeysForSpaceType = (st) =>
  safeArray(st?.default_item_type_keys ?? st?.default_item_keys ?? st?.default_items ?? st?.items ?? []).map(String);

function useUikitModal() {
  const ref = useRef(null);
  const show = useCallback(() => {
    if (!ref.current) return;
    UIkit.modal(ref.current).show();
  }, []);
  const hide = useCallback(() => {
    if (!ref.current) return;
    UIkit.modal(ref.current).hide();
  }, []);
  return { ref, show, hide };
}

// --------------------------
// Checks schema (your primitives)
// --------------------------
const CHECK_TYPES = [
  { type: "yes_no", label: "Yes / No" },
  { type: "text", label: "Text" },
  { type: "photo", label: "Photo" },
  { type: "number", label: "Count / Number" },
  { type: "rating", label: "Rating (1–5)" },
  { type: "choice", label: "Choice" },
  { type: "checklist", label: "Checklist" },
];

// Create a reasonable default check object per type
const newCheckForType = (type) => {
  const base = {
    key: "",
    type,
    label: "",
    required: false,
  };

  if (type === "rating") return { ...base, scale_min: 1, scale_max: 5 };
  if (type === "number") return { ...base, min: 0 };
  if (type === "choice") return { ...base, choices: ["option_1", "option_2"] };
  if (type === "checklist")
    return { ...base, steps: [{ key: "step_1", label: "Step 1" }] };

  return base;
};

// --------------------------
// Main Component
// --------------------------
const StandardsStructure = () => {
  const settingsCtx = React.useContext(AppContexts.SettingsContext) || {};
  const { set: setSettings } = settingsCtx;

  const [orgStandards, setOrgStandards] = useState(() => buildOrgStandardsFromLibrary());

  const deepClone = (v) => (typeof structuredClone === "function" ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

  const mergeDefaultsFromLibrary = (saved, lib) => {
    const next = deepClone(saved);

    const libByKey = {};
    safeArray(lib.item_types).forEach((it) => {
      if (it?.key) libByKey[it.key] = it;
    });

    next.item_types = safeArray(next.item_types).map((it) => {
      const k = it?.key;
      const src = libByKey[k];
      if (!src) return it;

      return {
        ...it,
        // only backfill if missing/empty in saved data
        checks: safeArray(it.checks).length ? safeArray(it.checks) : safeArray(src.checks),
        default_tasks: safeArray(it.default_tasks).length ? safeArray(it.default_tasks) : safeArray(src.default_tasks),
      };
    });

    return next;
  };

  const updateOrgStandards = useCallback(async (updater, toast = true) => {
    setOrgStandards((prev) => updater(deepClone(prev)));
    if (toast) UIkit.notification({ message: "Saved.", status: "success" });
  }, []);


  useEffect(() => {
    document.title = "Cleaning & Inspection Standards - RueBaRue";
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const lib = buildOrgStandardsFromLibrary();
        setOrgStandards(mergeDefaultsFromLibrary(parsed, lib));
      }
    } catch (e) {
      console.warn("Could not load standards from localStorage", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(orgStandards));
      } catch (e) {
        console.warn("Could not save standards to localStorage", e);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [orgStandards]);

  const spaceTypes = useMemo(() => safeArray(orgStandards?.space_types), [orgStandards]);
  const itemTypes = useMemo(() => safeArray(orgStandards?.item_types), [orgStandards]);

  const itemTypeByKey = useMemo(() => {
    const map = {};
    itemTypes.forEach((it) => {
      const k = entityKey(it);
      if (k) map[k] = it;
    });
    return map;
  }, [itemTypes]);

  const defaultItemsBySpaceKey = useMemo(() => {
    const map = {};
    spaceTypes.forEach((st, idx) => {
      const sk = entityKey(st) || `space-${idx}`;
      const itemKeys = defaultItemKeysForSpaceType(st);
      const missing = itemKeys.filter((k) => !itemTypeByKey[k]).length;
      map[sk] = { itemKeys, missing };
    });
    return map;
  }, [spaceTypes, itemTypeByKey]);

  const groupedSpaceTypes = useMemo(() => {
    const indoor = [];
    const outdoor = [];

    spaceTypes.forEach((st) => {
      const g = groupForSpaceType(st);
      const bucket = bucketForGroup(g);
      (bucket === "outdoor" ? outdoor : indoor).push(st);
    });

    const byLabel = (a, b) => labelFor(a).localeCompare(labelFor(b));
    indoor.sort(byLabel);
    outdoor.sort(byLabel);

    return { indoor, outdoor };
  }, [spaceTypes]);

  // --------------------------
  // Selection state
  // --------------------------
  const [selectedSpaceKey, setSelectedSpaceKey] = useState(null);
  const [selectedItemKey, setSelectedItemKey] = useState(null);

  const selectedSpaceType = useMemo(() => {
    if (!selectedSpaceKey) return null;
    return spaceTypes.find((st) => entityKey(st) === selectedSpaceKey) || null;
  }, [spaceTypes, selectedSpaceKey]);

  const itemKeysForSelectedSpace = useMemo(() => {
    if (!selectedSpaceKey) return [];
    return defaultItemsBySpaceKey[selectedSpaceKey]?.itemKeys || [];
  }, [defaultItemsBySpaceKey, selectedSpaceKey]);

  useEffect(() => {
    if (!selectedSpaceKey) return;

    // if no item is selected OR selected item isn't in this space anymore, pick the first
    const first = itemKeysForSelectedSpace[0] || null;

    if (!selectedItemKey || !itemKeysForSelectedSpace.includes(selectedItemKey)) {
      setSelectedItemKey(first);
    }
  }, [selectedSpaceKey, itemKeysForSelectedSpace, selectedItemKey]);

  const itemTypesForSelectedSpace = useMemo(() => {
    return itemKeysForSelectedSpace.map((k) => itemTypeByKey[k] || { key: k, label: k, __missing: true });
  }, [itemKeysForSelectedSpace, itemTypeByKey]);

  const selectedItemType = useMemo(() => {
    if (!selectedItemKey) return null;
    return itemTypeByKey[selectedItemKey] || { key: selectedItemKey, label: selectedItemKey, __missing: true };
  }, [selectedItemKey, itemTypeByKey]);

  const updateSpaceType = useCallback(
    async (spaceKey, patch) => {
      await updateOrgStandards((os) => {
        os.space_types = safeArray(os.space_types).map((s) => {
          if (entityKey(s) !== spaceKey) return s;
          return { ...s, ...patch };
        });
        return os;
      });
    },
    [updateOrgStandards]
  );

  const removeSpaceType = useCallback(
    async (spaceKey) => {
      await updateOrgStandards((os) => {
        os.space_types = safeArray(os.space_types).filter((s) => entityKey(s) !== spaceKey);

        // Cleanup: remove references from item_types.applies_to_space_type_keys
        os.item_types = safeArray(os.item_types).map((it) => ({
          ...it,
          applies_to_space_type_keys: safeArray(it.applies_to_space_type_keys).filter((k) => k !== spaceKey),
        }));

        return os;
      });

      setSelectedSpaceKey(null);
      setSelectedItemKey(null);
    },
    [updateOrgStandards]
  );

  const addSpaceType = useCallback(
    async ({ key, label, group_key }) => {
      const exists = safeArray(orgStandards?.space_types).some((s) => entityKey(s) === key);
      if (exists) {
        UIkit.notification({ message: "Space key already exists.", status: "danger" });
        return;
      }

      await updateOrgStandards((os) => {
        os.space_types = safeArray(os.space_types).concat({
          key,
          label,
          group_key: group_key || "indoor",
          default_item_type_keys: [],
        });
        return os;
      });

      setSelectedSpaceKey(key);
    },
    [orgStandards, updateOrgStandards]
  );

  const addItemType = useCallback(
    async ({ key, label }) => {
      const exists = safeArray(orgStandards?.item_types).some((it) => entityKey(it) === key);
      if (exists) {
        UIkit.notification({ message: "Item key already exists.", status: "danger" });
        return;
      }

      await updateOrgStandards((os) => {
        os.item_types = safeArray(os.item_types).concat({
          key,
          label,
          applies_to_space_type_keys: [],
          default_field_set_keys: [],
          tags: [],
          checks: [],
          default_tasks: [],
        });
        return os;
      });

      setSelectedItemKey(key);
    },
    [orgStandards, updateOrgStandards]
  );

  const updateItemType = useCallback(
    async (itemKey, patch) => {
      await updateOrgStandards((os) => {
        os.item_types = safeArray(os.item_types).map((it) => {
          if (entityKey(it) !== itemKey) return it;
          return { ...it, ...patch };
        });
        return os;
      });
    },
    [updateOrgStandards]
  );

  const removeItemType = useCallback(
    async (itemKey) => {
      await updateOrgStandards((os) => {
        os.item_types = safeArray(os.item_types).filter((it) => entityKey(it) !== itemKey);

        // Cleanup: remove from every space_types[].default_item_type_keys
        os.space_types = safeArray(os.space_types).map((st) => ({
          ...st,
          default_item_type_keys: safeArray(st.default_item_type_keys).filter((k) => k !== itemKey),
        }));

        return os;
      });

      if (selectedItemKey === itemKey) setSelectedItemKey(null);
    },
    [updateOrgStandards, selectedItemKey]
  );

  const addItemToSpace = useCallback(
    async (spaceKey, itemKey) => {
      await updateOrgStandards((os) => {
        os.space_types = safeArray(os.space_types).map((st) => {
          if (entityKey(st) !== spaceKey) return st;
          const next = new Set(safeArray(st.default_item_type_keys));
          next.add(itemKey);
          return { ...st, default_item_type_keys: Array.from(next) };
        });

        // (Optional) keep applies_to in sync (helpful)
        os.item_types = safeArray(os.item_types).map((it) => {
          if (entityKey(it) !== itemKey) return it;
          const next = new Set(safeArray(it.applies_to_space_type_keys));
          next.add(spaceKey);
          return { ...it, applies_to_space_type_keys: Array.from(next) };
        });

        return os;
      });
    },
    [updateOrgStandards]
  );

  const removeItemFromSpace = useCallback(
    async (spaceKey, itemKey) => {
      await updateOrgStandards((os) => {
        os.space_types = safeArray(os.space_types).map((st) => {
          if (entityKey(st) !== spaceKey) return st;
          return { ...st, default_item_type_keys: safeArray(st.default_item_type_keys).filter((k) => k !== itemKey) };
        });

        // (Optional) also remove applies_to mapping
        os.item_types = safeArray(os.item_types).map((it) => {
          if (entityKey(it) !== itemKey) return it;
          return { ...it, applies_to_space_type_keys: safeArray(it.applies_to_space_type_keys).filter((k) => k !== spaceKey) };
        });

        return os;
      });

      if (selectedItemKey === itemKey) setSelectedItemKey(null);
    },
    [updateOrgStandards, selectedItemKey]
  );

  // --------------------------
  // CRUD helpers (Item Checks)
  // --------------------------
  const addCheckToItem = useCallback(
    async (itemKey, check) => {
      await updateOrgStandards((os) => {
        os.item_types = safeArray(os.item_types).map((it) => {
          if (entityKey(it) !== itemKey) return it;
          const existing = safeArray(it.checks);
          return { ...it, checks: existing.concat(check) };
        });
        return os;
      });
    },
    [updateOrgStandards]
  );

  const updateCheckOnItem = useCallback(
    async (itemKey, checkKey, patch) => {
      await updateOrgStandards((os) => {
        os.item_types = safeArray(os.item_types).map((it) => {
          if (entityKey(it) !== itemKey) return it;
          const nextChecks = safeArray(it.checks).map((c) => (c.key === checkKey ? { ...c, ...patch } : c));
          return { ...it, checks: nextChecks };
        });
        return os;
      });
    },
    [updateOrgStandards]
  );

  const removeCheckFromItem = useCallback(
    async (itemKey, checkKey) => {
      await updateOrgStandards((os) => {
        os.item_types = safeArray(os.item_types).map((it) => {
          if (entityKey(it) !== itemKey) return it;
          return { ...it, checks: safeArray(it.checks).filter((c) => c.key !== checkKey) };
        });
        return os;
      });
    },
    [updateOrgStandards]
  );

  // --------------------------
  // Modals: Space, Item, Add-to-space, Check editor
  // --------------------------
  const spaceModal = useUikitModal();
  const itemModal = useUikitModal();
  const addToSpaceModal = useUikitModal();
  const checkModal = useUikitModal();

  const [spaceForm, setSpaceForm] = useState({ mode: "add", key: "", label: "", group_key: "indoor" });
  const [itemForm, setItemForm] = useState({ mode: "add", key: "", label: "" });

  const [addToSpaceForm, setAddToSpaceForm] = useState({ itemKey: "" });

  const [checkForm, setCheckForm] = useState({
    mode: "add", // add | edit
    itemKey: "",
    originalKey: "",
    data: newCheckForType("yes_no"),
  });

  const openAddSpace = () => {
    setSpaceForm({ mode: "add", key: "", label: "", group_key: "indoor" });
    spaceModal.show();
  };
  const openEditSpace = (st) => {
    setSpaceForm({
      mode: "edit",
      key: entityKey(st),
      label: st?.label || "",
      group_key: st?.group_key || "indoor",
    });
    spaceModal.show();
  };

  const openAddItem = () => {
    setItemForm({ mode: "add", key: "", label: "" });
    itemModal.show();
  };
  const openEditItem = (it) => {
    setItemForm({ mode: "edit", key: entityKey(it), label: it?.label || "" });
    itemModal.show();
  };

  const openAddItemToSpace = () => {
    setAddToSpaceForm({ itemKey: "" });
    addToSpaceModal.show();
  };

  const openAddCheck = (itemKey) => {
    setCheckForm({
      mode: "add",
      itemKey,
      originalKey: "",
      data: newCheckForType("yes_no"),
    });
    checkModal.show();
  };
  const openEditCheck = (itemKey, check) => {
    setCheckForm({
      mode: "edit",
      itemKey,
      originalKey: check.key,
      data: structuredClone(check),
    });
    checkModal.show();
  };

  // Available item keys to add to selected space (exclude ones already attached)
  const availableItemKeysForSelectedSpace = useMemo(() => {
    if (!selectedSpaceKey) return [];
    const already = new Set(itemKeysForSelectedSpace);
    return itemTypes
      .map((it) => entityKey(it))
      .filter(Boolean)
      .filter((k) => !already.has(k))
      .sort((a, b) => (itemTypeByKey[a]?.label || a).localeCompare(itemTypeByKey[b]?.label || b));
  }, [selectedSpaceKey, itemKeysForSelectedSpace, itemTypes, itemTypeByKey]);

  const saveSpace = async (e) => {
    e.preventDefault();
    const label = (spaceForm.label || "").trim();
    if (!label) return UIkit.notification({ message: "Space label required.", status: "danger" });

    try {
      if (spaceForm.mode === "add") {
        const existing = new Set(safeArray(orgStandards?.space_types).map((s) => entityKey(s)).filter(Boolean));
        const base = slugifyKey(label);
        const key = uniqueKey(base, existing);

        await addSpaceType({ key, label, group_key: spaceForm.group_key });
      } else {
        // edit uses existing key stored in spaceForm.key (hidden)
        await updateSpaceType(spaceForm.key, { label, group_key: spaceForm.group_key });
      }
      spaceModal.hide();
    } catch (err) {
      UIkit.notification({ message: err?.message || "Could not save space.", status: "danger" });
    }
  };

  const saveItem = async (e) => {
    e.preventDefault();
    const label = (itemForm.label || "").trim();
    if (!label) return UIkit.notification({ message: "Item label required.", status: "danger" });

    try {
      if (itemForm.mode === "add") {
        const existing = new Set(safeArray(orgStandards?.item_types).map((it) => entityKey(it)).filter(Boolean));
        const base = slugifyKey(label);
        const key = uniqueKey(base, existing);

        await addItemType({ key, label });
      } else {
        await updateItemType(itemForm.key, { label });
      }
      itemModal.hide();
    } catch (err) {
      UIkit.notification({ message: err?.message || "Could not save item.", status: "danger" });
    }
  };
  const saveAddItemToSpace = async (e) => {
    e.preventDefault();
    if (!selectedSpaceKey) return;
    const itemKey = (addToSpaceForm.itemKey || "").trim();
    if (!itemKey) return UIkit.notification({ message: "Choose an item to add.", status: "danger" });
    await addItemToSpace(selectedSpaceKey, itemKey);
    addToSpaceModal.hide();
  };

  const saveCheck = async (e) => {
    e.preventDefault();

    const data = structuredClone(checkForm.data);
    data.key = (data.key || "").trim();
    data.label = (data.label || "").trim();

    if (!checkForm.itemKey) return;
    if (!data.key || !data.label) {
      return UIkit.notification({ message: "Check key + label required.", status: "danger" });
    }

    // basic validation for type-specific fields
    if (data.type === "choice") {
      data.choices = safeArray(data.choices).map((c) => String(c).trim()).filter(Boolean);
      if (data.choices.length < 2) return UIkit.notification({ message: "Choice needs at least 2 options.", status: "danger" });
    }
    if (data.type === "checklist") {
      data.steps = safeArray(data.steps)
        .map((s) => ({ key: String(s.key || "").trim(), label: String(s.label || "").trim() }))
        .filter((s) => s.key && s.label);
      if (data.steps.length < 1) return UIkit.notification({ message: "Checklist needs at least 1 step.", status: "danger" });
    }

    const it = itemTypeByKey[checkForm.itemKey];
    const existing = safeArray(it?.checks);

    if (checkForm.mode === "add") {
      if (existing.some((c) => c.key === data.key)) {
        return UIkit.notification({ message: "That check key already exists for this item.", status: "danger" });
      }
      await addCheckToItem(checkForm.itemKey, data);
    } else {
      // editing: allow changing key only if it doesn't collide
      if (data.key !== checkForm.originalKey && existing.some((c) => c.key === data.key)) {
        return UIkit.notification({ message: "That check key already exists for this item.", status: "danger" });
      }
      // if key changed, do remove+add (simple & safe)
      if (data.key !== checkForm.originalKey) {
        await removeCheckFromItem(checkForm.itemKey, checkForm.originalKey);
        await addCheckToItem(checkForm.itemKey, data);
      } else {
        await updateCheckOnItem(checkForm.itemKey, checkForm.originalKey, data);
      }
    }

    checkModal.hide();
  };

  const selectedChecks = useMemo(
    () => safeArray(selectedItemType?.checks),
    [selectedItemType]
  );

  const selectedTasks = useMemo(
    () => safeArray(selectedItemType?.default_tasks),
    [selectedItemType]
  );  

  return (
    <section>

      <div id="hms-page-title" className="uk-flex uk-flex-between uk-flex-middle">
        <div>
          <h1 className="uk-heading-small">Cleaning & Inspection Standards</h1>
        </div>
      </div>

      <div id="hms-main-body">
        <div className="uk-grid-small" uk-grid="true">
          <div className="uk-width-1-4@l uk-width-1-3@m uk-width-1-1">
            <div className="uk-card uk-card-default uk-card-small">
              <div className="uk-card-header uk-padding-small uk-flex uk-flex-between uk-flex-middle">
                <div>
                  <div className="uk-text-bold">Space</div>
                  <div className="uk-text-meta">Indoor / Outdoor</div>
                </div>

                <button className="uk-button uk-button-small hms-btn hms-gray-btn" type="button" onClick={openAddSpace}>
                  + Space
                </button>
              </div>

              <div className="uk-card-body uk-padding-small">
                <div className="uk-margin-small">
                  <div className="uk-text-uppercase uk-text-meta uk-margin-small-bottom">Indoor</div>
                  <ul className="uk-list uk-list-divider">
                    {groupedSpaceTypes.indoor.map((st, idx) => {
                      const sk = entityKey(st) || `space-${idx}`;
                      const pack = defaultItemsBySpaceKey[sk] || { itemKeys: [], missing: 0 };

                      return (
                        <li key={stableKey("st-in", sk, idx)}>
                          <div className="uk-flex uk-flex-between uk-flex-middle">
                            <button
                              className={`uk-button uk-button-text uk-text-left ${
                                sk === selectedSpaceKey ? "uk-text-bold" : ""
                              }`}
                              style={{ flex: 1 }}
                              onClick={() => {
                                setSelectedSpaceKey(sk);
                                setSelectedItemKey(null);
                              }}
                            >
                              {labelFor(st)} <span className="uk-text-meta">({pack.itemKeys.length})</span>
                              {pack.missing > 0 && (
                                <span className="uk-label uk-label-warning uk-margin-small-left">{pack.missing} missing</span>
                              )}
                            </button>

                            <div className="uk-flex" style={{ gap: 6 }}>
                              <button
                                className="uk-button uk-button-text uk-text-muted"
                                type="button"
                                title="Edit"
                                onClick={() => openEditSpace(st)}
                              >
                                ✎
                              </button>
                              <button
                                className="uk-button uk-button-text uk-text-danger"
                                type="button"
                                title="Remove"
                                onClick={() => {
                                  UIkit.modal.confirm(`Remove space type "${labelFor(st)}"?`).then(() => removeSpaceType(sk));
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="uk-margin-small">
                  <div className="uk-text-uppercase uk-text-meta uk-margin-small-bottom">Outdoor</div>
                  <ul className="uk-list uk-list-divider">
                    {groupedSpaceTypes.outdoor.map((st, idx) => {
                      const sk = entityKey(st) || `space-${idx}`;
                      const pack = defaultItemsBySpaceKey[sk] || { itemKeys: [], missing: 0 };

                      return (
                        <li key={stableKey("st-out", sk, idx)}>
                          <div className="uk-flex uk-flex-between uk-flex-middle">
                            <button
                              className={`uk-button uk-button-text uk-text-left ${
                                sk === selectedSpaceKey ? "uk-text-bold" : ""
                              }`}
                              style={{ flex: 1 }}
                              onClick={() => {
                                setSelectedSpaceKey(sk);
                                setSelectedItemKey(null);
                              }}
                            >
                              {labelFor(st)} <span className="uk-text-meta">({pack.itemKeys.length})</span>
                              {pack.missing > 0 && (
                                <span className="uk-label uk-label-warning uk-margin-small-left">{pack.missing} missing</span>
                              )}
                            </button>

                            <div className="uk-flex" style={{ gap: 6 }}>
                              <button
                                className="uk-button uk-button-text uk-text-muted"
                                type="button"
                                title="Edit"
                                onClick={() => openEditSpace(st)}
                              >
                                ✎
                              </button>
                              <button
                                className="uk-button uk-button-text uk-text-danger"
                                type="button"
                                title="Remove"
                                onClick={() => {
                                  UIkit.modal.confirm(`Remove space type "${labelFor(st)}"?`).then(() => removeSpaceType(sk));
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* ---------------- Column 2: Items in Selected Space ---------------- */}
          <div className="uk-width-1-4@l uk-width-1-3@m uk-width-1-1">
            <div className="uk-card uk-card-default uk-card-small">
              <div className="uk-card-header uk-padding-small uk-flex uk-flex-between uk-flex-middle">
                <div>
                  <div className="uk-text-bold">Items</div>
                  <div className="uk-text-meta">{selectedSpaceType ? labelFor(selectedSpaceType) : "Pick a space"}</div>
                </div>

                {selectedSpaceKey ? (
                  <div className="uk-flex" style={{ gap: 8 }}>
                    <button className="uk-button hms-btn hms-gray-btn" type="button" onClick={openAddItem}>
                      + Item
                    </button>
                  </div>                  
                ) : null}
              </div>

              <div className="uk-card-body uk-padding-small">
                {!selectedSpaceType && <div className="uk-text-meta uk-padding-small">Pick a space type.</div>}

                {selectedSpaceType && itemTypesForSelectedSpace.length === 0 && (
                  <div className="uk-text-meta uk-padding-small">No items yet. Click “+ Add”.</div>
                )}

                {selectedSpaceType && itemTypesForSelectedSpace.length > 0 && (
                  <ul className="uk-list uk-list-divider">
                    {itemTypesForSelectedSpace.map((it, idx) => {
                      const ik = entityKey(it);
                      const tasksCount = safeArray(it?.default_tasks).length;

                      return (
                        <li key={stableKey("it", ik, idx)}>
                          <div className="uk-flex uk-flex-between uk-flex-middle">
                            <button
                              className={`uk-button uk-button-text uk-text-left ${
                                ik === selectedItemKey ? "uk-text-bold" : ""
                              }`}
                              style={{ flex: 1 }}
                              onClick={() => setSelectedItemKey(ik)}
                            >
                              {labelFor(it)}{" "}
                              <span className="uk-text-meta">({tasksCount} tasks)</span>{" "}
                              {it.__missing && <span className="uk-label uk-label-warning uk-margin-small-left">Missing</span>}
                            </button>

                            {!it.__missing && (
                              <div className="uk-flex" style={{ gap: 6 }}>
                                <button
                                  className="uk-button uk-button-text uk-text-muted"
                                  type="button"
                                  title="Edit item type"
                                  onClick={() => openEditItem(it)}
                                >
                                  ✎
                                </button>
                                <button
                                  className="uk-button uk-button-text uk-text-danger"
                                  type="button"
                                  title="Remove from this space"
                                  onClick={() => {
                                    UIkit.modal
                                      .confirm(`Remove "${labelFor(it)}" from ${labelFor(selectedSpaceType)}?`)
                                      .then(() => removeItemFromSpace(selectedSpaceKey, ik));
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* ---------------- Column 3: Item Details + Default Checks ---------------- */}
          <div className="uk-width-1-2@l uk-width-1-3@m uk-width-1-1">
            <div className="uk-card uk-card-default uk-card-small">
              <div className="uk-card-header uk-padding-small uk-flex uk-flex-between uk-flex-middle">
                <div>
                  <div className="uk-text-bold">Tasks</div>
                  <div className="uk-text-meta">Default tasks/checks for this item type</div>
                </div>

                <button
                  className="uk-button uk-button-small hms-btn hms-gray-btn"
                  type="button"
                  disabled={!selectedItemKey || selectedItemType?.__missing}
                  onClick={() => openAddCheck(selectedItemKey)}
                >
                  + Check
                </button>
              </div>

              <div className="uk-card-body uk-padding-small">
                {!selectedItemKey && <div className="uk-text-meta uk-padding-small">Select an item.</div>}

                {selectedItemKey && selectedItemType?.__missing && (
                  <div className="uk-alert uk-alert-warning" uk-alert="true">
                    This item key is referenced by a space type, but it does not exist in <code>item_types</code>.
                    Add it as an Item Type (left column) to manage it.
                  </div>
                )}

                {selectedItemKey && !selectedItemType?.__missing && (
                  <>
                    <div className="uk-text-bold">{labelFor(selectedItemType)}</div>

                    <div className="uk-flex uk-flex-between uk-flex-middle">
                      <div className="uk-text-meta">Default Checks</div>
                      <div className="uk-text-meta">{selectedChecks.length} total</div>
                    </div>

                    {selectedChecks.length === 0 ? (
                      <div className="uk-text-meta uk-padding-small">
                        No checks yet. Click “+ Check” to add Condition / Checklist / Photo / Count / Text / YesNo / Rating.
                      </div>
                    ) : (
                      <ul className="uk-list uk-list-divider uk-margin-small-top">
                        {selectedChecks.map((c, idx) => (
                          <li key={stableKey("chk", c.key, idx)}>
                            <div className="uk-flex uk-flex-between uk-flex-middle">
                              <div>
                                <div className="uk-text-bold">{c.label}</div>
                                <div className="uk-text-meta">
                                  <span className="uk-label uk-label-default">{c.type}</span>{" "}
                                  <span className="uk-text-meta">key: {c.key}</span>{" "}
                                  {c.required ? <span className="uk-label uk-label-warning uk-margin-small-left">required</span> : null}
                                </div>
                              </div>

                              <div className="uk-flex" style={{ gap: 8 }}>
                                <button className="uk-button uk-button-text" type="button" onClick={() => openEditCheck(selectedItemKey, c)}>
                                  Edit
                                </button>
                                <button
                                  className="uk-button uk-button-text uk-text-danger"
                                  type="button"
                                  onClick={() => {
                                    UIkit.modal.confirm(`Remove check "${c.label}"?`).then(() => removeCheckFromItem(selectedItemKey, c.key));
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>

                            {/* small preview */}
                            {c.type === "choice" && (
                              <div className="uk-text-meta">choices: {safeArray(c.choices).join(", ")}</div>
                            )}
                            {c.type === "checklist" && (
                              <div className="uk-text-meta">steps: {safeArray(c.steps).map((s) => s.label).join(" • ")}</div>
                            )}
                            {c.type === "rating" && (
                              <div className="uk-text-meta">scale: {c.scale_min}–{c.scale_max}</div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    <hr />

                    <div className="uk-flex uk-flex-between uk-flex-middle">
                      <div className="uk-text-meta">Default Tasks</div>
                      <div className="uk-text-meta">{selectedTasks.length} total</div>
                    </div>

                    {selectedTasks.length === 0 ? (
                      <div className="uk-text-meta uk-padding-small">No default tasks yet for this item.</div>
                    ) : (
                      <ul className="uk-list uk-list-divider uk-margin-small-top">
                        {selectedTasks.map((t, idx) => (
                          <li key={stableKey("task", t.key || t.label, idx)}>
                            <div className="uk-text-bold">{t.label}</div>
                            <div className="uk-text-meta">
                              <span className="uk-label uk-label-default">{t.task_type_key}</span>
                              {safeArray(t.field_set_keys).length ? (
                                <span className="uk-margin-small-left">fields: {safeArray(t.field_set_keys).join(", ")}</span>
                              ) : null}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}

                    <hr />
                    <div className="hms-add-tag">
                      <div className="uk-form-label">Tags</div>
                      <div className="uk-flex uk-flex-top">                    
                        <Tags
                          value={safeArray(selectedItemType?.tags)}
                          onChange={(nextTags) => {
                            if (!selectedItemKey || selectedItemType?.__missing) return;

                            const cleaned = Array.from(
                              new Set(safeArray(nextTags).map((t) => String(t).trim()).filter(Boolean))
                            );

                            updateItemType(selectedItemKey, { tags: cleaned });
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Debug quick view
            <div className="uk-card uk-card-default uk-card-body uk-margin-small-top">
              <div className="uk-text-bold">Debug</div>
              <div className="uk-text-meta">
                spaces={spaceTypes.length} • items={itemTypes.length} • localStorage key=<code>{LS_KEY}</code>
              </div>
              <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                {JSON.stringify(
                  {
                    selectedSpaceKey,
                    selectedItemKey,
                    selectedSpaceType: selectedSpaceType || null,
                    selectedItemType: selectedItemType || null,
                  },
                  null,
                  2
                )}
              </pre>
            </div>
            */}
          </div>
        </div>
      </div>

      {/* =========================================================
          MODALS
         ========================================================= */}

      {/* Space modal */}
      <div ref={spaceModal.ref} id="modal-space" uk-modal="true">
        <div className="uk-modal-dialog uk-modal-body">
          <h3 className="uk-h4 uk-margin-remove">{spaceForm.mode === "add" ? "Add Space Type" : "Edit Space Type"}</h3>

          <form className="uk-form-stacked uk-margin-top" onSubmit={saveSpace}>
            <div className="uk-margin">
              <label className="uk-form-label">Key</label>
              <div className="uk-form-controls">
                <input
                  className="uk-input"
                  value={spaceForm.key}
                  disabled={spaceForm.mode === "edit"}
                  onChange={(e) => setSpaceForm((s) => ({ ...s, key: e.target.value }))}
                  placeholder="e.g., mud_room"
                />
                {spaceForm.mode === "edit" && <div className="uk-text-meta">Key can’t be edited in this quick editor.</div>}
              </div>
            </div>

            <div className="uk-margin">
              <label className="uk-form-label">Label</label>
              <div className="uk-form-controls">
                <input
                  className="uk-input"
                  value={spaceForm.label}
                  onChange={(e) => setSpaceForm((s) => ({ ...s, label: e.target.value }))}
                  placeholder="e.g., Mud Room"
                />
              </div>
            </div>

            <div className="uk-margin">
              <label className="uk-form-label">Group</label>
              <div className="uk-form-controls">
                <select
                  className="uk-select"
                  value={spaceForm.group_key}
                  onChange={(e) => setSpaceForm((s) => ({ ...s, group_key: e.target.value }))}
                >
                  <option value="indoor">Indoor</option>
                  <option value="outdoor">Outdoor</option>
                  <option value="amenities">Amenities</option>
                  <option value="utility">Utility</option>
                </select>
              </div>
            </div>

            <div className="uk-flex uk-flex-right" style={{ gap: 8 }}>
              <button type="button" className="uk-button uk-button-default uk-modal-close">
                Cancel
              </button>
              <button type="submit" className="uk-button hms-btn hms-red-btn">
                Save
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Item modal */}
      <div ref={itemModal.ref} id="modal-item" uk-modal="true">
        <div className="uk-modal-dialog uk-modal-body">
          <h3 className="uk-h4 uk-margin-remove">{itemForm.mode === "add" ? "Add Item Type" : "Edit Item Type"}</h3>

          <form className="uk-form-stacked" onSubmit={saveItem}>
            <div className="uk-margin">
              <label className="uk-form-label">Key</label>
              <div className="uk-form-controls">
                <input
                  className="uk-input"
                  value={itemForm.key}
                  disabled={itemForm.mode === "edit"}
                  onChange={(e) => setItemForm((s) => ({ ...s, key: e.target.value }))}
                  placeholder="e.g., coffee_table"
                />
                {itemForm.mode === "edit" && <div className="uk-text-meta">Key can’t be edited in this quick editor.</div>}
              </div>
            </div>

            <div className="uk-margin">
              <label className="uk-form-label">Label</label>
              <div className="uk-form-controls">
                <input
                  className="uk-input"
                  value={itemForm.label}
                  onChange={(e) => setItemForm((s) => ({ ...s, label: e.target.value }))}
                  placeholder="e.g., Coffee Table"
                />
              </div>
            </div>

            <div className="uk-flex uk-flex-right" style={{ gap: 8 }}>
              <button type="button" className="uk-button uk-button-default uk-modal-close">
                Cancel
              </button>
              <button type="submit" className="uk-button hms-btn hms-red-btn">
                Save
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Add item to selected space */}
      <div ref={addToSpaceModal.ref} id="modal-add-to-space" uk-modal="true">
        <div className="uk-modal-dialog uk-modal-body">
          <h3 className="uk-h4 uk-margin-remove">Add Item to {selectedSpaceType ? labelFor(selectedSpaceType) : "Space"}</h3>

          <form className="uk-form-stacked" onSubmit={saveAddItemToSpace}>
            <div className="uk-margin">
              <label className="uk-form-label">Item</label>
              <div className="uk-form-controls">
                <select
                  className="uk-select"
                  value={addToSpaceForm.itemKey}
                  onChange={(e) => setAddToSpaceForm({ itemKey: e.target.value })}
                >
                  <option value="">Select an item type…</option>
                  {availableItemKeysForSelectedSpace.map((k) => (
                    <option key={k} value={k}>
                      {itemTypeByKey[k]?.label || k} ({k})
                    </option>
                  ))}
                </select>

                {!availableItemKeysForSelectedSpace.length && (
                  <div className="uk-text-meta uk-margin-small-top">All item types are already attached to this space.</div>
                )}
              </div>
            </div>

            <div className="uk-flex uk-flex-right" style={{ gap: 8 }}>
              <button type="button" className="uk-button uk-button-default uk-modal-close">
                Cancel
              </button>
              <button type="submit" className="uk-button hms-btn hms-red-btn" disabled={!availableItemKeysForSelectedSpace.length}>
                Add
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Check editor modal */}
      <div ref={checkModal.ref} id="modal-check" uk-modal="true">
        <div className="uk-modal-dialog uk-modal-body">
          <h3 className="uk-h4 uk-margin-remove">
            {checkForm.mode === "add" ? "Add Default Check" : "Edit Default Check"}
          </h3>

          <form className="uk-form-stacked" onSubmit={saveCheck}>
            <div className="uk-grid-small uk-child-width-1-2@s" uk-grid="true">
              <div>
                <label className="uk-form-label">Type</label>
                <div className="uk-form-controls">
                  <select
                    className="uk-select"
                    value={checkForm.data.type}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setCheckForm((s) => ({
                        ...s,
                        data: {
                          ...newCheckForType(nextType),
                          // keep key/label if user already typed them
                          key: s.data.key,
                          label: s.data.label,
                          required: s.data.required,
                        },
                      }));
                    }}
                  >
                    {CHECK_TYPES.map((t) => (
                      <option key={t.type} value={t.type}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="uk-flex uk-flex-middle">
                <label className="uk-form-label" style={{ marginBottom: 0 }}>
                  Required?
                </label>
                <div className="uk-form-controls uk-margin-small-left">
                  <input
                    className="uk-checkbox"
                    type="checkbox"
                    checked={!!checkForm.data.required}
                    onChange={(e) => setCheckForm((s) => ({ ...s, data: { ...s.data, required: e.target.checked } }))}
                  />
                </div>
              </div>
            </div>

            <div className="uk-margin">
              <label className="uk-form-label">Key</label>
              <div className="uk-form-controls">
                <input
                  className="uk-input"
                  value={checkForm.data.key}
                  onChange={(e) => setCheckForm((s) => ({ ...s, data: { ...s.data, key: e.target.value } }))}
                  placeholder="e.g., is_working"
                />
              </div>
            </div>

            <div className="uk-margin">
              <label className="uk-form-label">Label</label>
              <div className="uk-form-controls">
                <input
                  className="uk-input"
                  value={checkForm.data.label}
                  onChange={(e) => setCheckForm((s) => ({ ...s, data: { ...s.data, label: e.target.value } }))}
                  placeholder="e.g., Working?"
                />
              </div>
            </div>

            {/* Type-specific fields */}
            {checkForm.data.type === "choice" && (
              <div className="uk-margin">
                <label className="uk-form-label">Choices (comma separated)</label>
                <div className="uk-form-controls">
                  <input
                    className="uk-input"
                    value={safeArray(checkForm.data.choices).join(", ")}
                    onChange={(e) =>
                      setCheckForm((s) => ({
                        ...s,
                        data: { ...s.data, choices: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) },
                      }))
                    }
                    placeholder="clean, dirty"
                  />
                </div>
              </div>
            )}

            {checkForm.data.type === "number" && (
              <div className="uk-grid-small uk-child-width-1-2@s" uk-grid="true">
                <div>
                  <label className="uk-form-label">Min</label>
                  <div className="uk-form-controls">
                    <input
                      className="uk-input"
                      type="number"
                      value={checkForm.data.min ?? 0}
                      onChange={(e) =>
                        setCheckForm((s) => ({ ...s, data: { ...s.data, min: Number(e.target.value) } }))
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="uk-form-label">Max (optional)</label>
                  <div className="uk-form-controls">
                    <input
                      className="uk-input"
                      type="number"
                      value={checkForm.data.max ?? ""}
                      onChange={(e) =>
                        setCheckForm((s) => ({
                          ...s,
                          data: { ...s.data, max: e.target.value === "" ? undefined : Number(e.target.value) },
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {checkForm.data.type === "rating" && (
              <div className="uk-grid-small uk-child-width-1-2@s" uk-grid="true">
                <div>
                  <label className="uk-form-label">Scale Min</label>
                  <div className="uk-form-controls">
                    <input
                      className="uk-input"
                      type="number"
                      value={checkForm.data.scale_min ?? 1}
                      onChange={(e) =>
                        setCheckForm((s) => ({ ...s, data: { ...s.data, scale_min: Number(e.target.value) } }))
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="uk-form-label">Scale Max</label>
                  <div className="uk-form-controls">
                    <input
                      className="uk-input"
                      type="number"
                      value={checkForm.data.scale_max ?? 5}
                      onChange={(e) =>
                        setCheckForm((s) => ({ ...s, data: { ...s.data, scale_max: Number(e.target.value) } }))
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {checkForm.data.type === "checklist" && (
              <div className="uk-margin">
                <label className="uk-form-label">Checklist Steps</label>
                <div className="uk-form-controls">
                  {safeArray(checkForm.data.steps).map((step, idx) => (
                    <div key={stableKey("step", step.key, idx)} className="uk-grid-small uk-child-width-1-2@s" uk-grid="true">
                      <div>
                        <input
                          className="uk-input"
                          value={step.key}
                          placeholder="step_key"
                          onChange={(e) => {
                            const next = safeArray(checkForm.data.steps);
                            next[idx] = { ...next[idx], key: e.target.value };
                            setCheckForm((s) => ({ ...s, data: { ...s.data, steps: next } }));
                          }}
                        />
                      </div>
                      <div>
                        <input
                          className="uk-input"
                          value={step.label}
                          placeholder="Step label"
                          onChange={(e) => {
                            const next = safeArray(checkForm.data.steps);
                            next[idx] = { ...next[idx], label: e.target.value };
                            setCheckForm((s) => ({ ...s, data: { ...s.data, steps: next } }));
                          }}
                        />
                      </div>
                      <div className="uk-width-auto@s">
                        <button
                          type="button"
                          className="uk-button uk-button-text uk-text-danger"
                          onClick={() => {
                            const next = safeArray(checkForm.data.steps).filter((_, i) => i !== idx);
                            setCheckForm((s) => ({ ...s, data: { ...s.data, steps: next } }));
                          }}
                        >
                          remove
                        </button>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="uk-button uk-button-small hms-btn hms-gray-btn uk-margin-small-top"
                    onClick={() => {
                      const next = safeArray(checkForm.data.steps);
                      next.push({ key: `step_${next.length + 1}`, label: `Step ${next.length + 1}` });
                      setCheckForm((s) => ({ ...s, data: { ...s.data, steps: next } }));
                    }}
                  >
                    + Step
                  </button>
                </div>
              </div>
            )}

            {/* Optional: fail behavior */}
            <div className="uk-margin">
              <label className="uk-form-label">On Fail (optional)</label>
              <div className="uk-form-controls uk-grid-small uk-child-width-1-2@s" uk-grid="true">
                <div>
                  <input
                    className="uk-input"
                    value={checkForm.data.fail_creates_task_type || ""}
                    onChange={(e) =>
                      setCheckForm((s) => ({ ...s, data: { ...s.data, fail_creates_task_type: e.target.value || undefined } }))
                    }
                    placeholder="task type key (e.g., repair)"
                  />
                </div>
                <div>
                  <input
                    className="uk-input"
                    value={checkForm.data.fail_task_label || ""}
                    onChange={(e) =>
                      setCheckForm((s) => ({ ...s, data: { ...s.data, fail_task_label: e.target.value || undefined } }))
                    }
                    placeholder='task label (e.g., "Repair needed")'
                  />
                </div>
              </div>
              <div className="uk-text-meta uk-margin-small-top">
                Later, your “run” flow can auto-create follow-up tasks when checks fail.
              </div>
            </div>

            <div className="uk-flex uk-flex-right" style={{ gap: 8 }}>
              <button type="button" className="uk-button uk-button-default uk-modal-close">
                Cancel
              </button>
              <button type="submit" className="uk-button hms-btn hms-red-btn">
                Save Check
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default StandardsStructure;